import React, { useState, useRef, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { authAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { User, Mail, Building, Phone, Shield, Lock, CheckCircle2, Camera, Loader2, Trash2, CreditCard, Plus, Star, AlertTriangle, Bell, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

// Card brand icons/colors
const CARD_BRANDS = {
  visa: { name: 'Visa', color: 'bg-blue-600' },
  mastercard: { name: 'Mastercard', color: 'bg-orange-500' },
  amex: { name: 'American Express', color: 'bg-blue-400' },
  discover: { name: 'Discover', color: 'bg-orange-400' },
  default: { name: 'Card', color: 'bg-slate-600' }
};

export default function Profile() {
  const { user, refreshUser, token } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    company_name: user?.company_name || '',
    phone: user?.phone || '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [addingCard, setAddingCard] = useState(false);
  const [newCard, setNewCard] = useState({
    cardholder_name: '',
    last_four: '',
    brand: 'visa',
    exp_month: '',
    exp_year: '',
    set_as_default: false
  });

  // Email preferences state
  const [emailPrefs, setEmailPrefs] = useState({
    monthly_summary_enabled: true,
    renewal_reminders_enabled: true,
    promotional_emails_enabled: true
  });
  const [loadingEmailPrefs, setLoadingEmailPrefs] = useState(true);
  const [sendingReport, setSendingReport] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  // Get the correct user ID field (could be 'id' or 'user_id')
  const getUserId = () => user?.id || user?.user_id;

  const getAvatarUrl = () => {
    if (user?.avatar_url) {
      if (user.avatar_url.startsWith('http')) return user.avatar_url;
      return `${BACKEND_URL}${user.avatar_url}`;
    }
    return null;
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error('Image too large. Maximum size is 2MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use JPG, PNG, or WebP');
      return;
    }

    setUploadingAvatar(true);
    try {
      await usersAPI.uploadAvatar(getUserId(), file);
      toast.success('Avatar updated successfully');
      // Refresh user data to get new avatar URL
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Remove your profile picture?')) return;
    
    try {
      await usersAPI.deleteAvatar(getUserId());
      toast.success('Avatar removed');
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error('Failed to remove avatar');
    }
  };

  // Payment Methods Functions
  const loadPaymentMethods = async () => {
    setLoadingPayments(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/methods`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data.payment_methods || []);
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadPaymentMethods();
      loadEmailPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAddCard = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!newCard.last_four || newCard.last_four.length !== 4) {
      toast.error('Please enter the last 4 digits of your card');
      return;
    }
    if (!newCard.exp_month || !newCard.exp_year) {
      toast.error('Please enter card expiration date');
      return;
    }
    
    setAddingCard(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/methods`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCard)
      });
      
      if (response.ok) {
        toast.success('Payment method added');
        setShowAddCard(false);
        setNewCard({
          cardholder_name: '',
          last_four: '',
          brand: 'visa',
          exp_month: '',
          exp_year: '',
          set_as_default: false
        });
        loadPaymentMethods();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to add card');
      }
    } catch (err) {
      toast.error('Failed to add payment method');
    } finally {
      setAddingCard(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/methods/${methodId}/default`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Default payment method updated');
        loadPaymentMethods();
      }
    } catch (err) {
      toast.error('Failed to update default');
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    if (!window.confirm('Remove this payment method?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/methods/${methodId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Payment method removed');
        loadPaymentMethods();
      }
    } catch (err) {
      toast.error('Failed to remove payment method');
    }
  };

  const isCardExpiringSoon = (expMonth, expYear) => {
    const now = new Date();
    const expDate = new Date(expYear, expMonth - 1);
    const diffMonths = (expDate.getFullYear() - now.getFullYear()) * 12 + (expDate.getMonth() - now.getMonth());
    return diffMonths <= 2 && diffMonths >= 0;
  };

  const isCardExpired = (expMonth, expYear) => {
    const now = new Date();
    const expDate = new Date(expYear, expMonth);
    return expDate < now;
  };

  // Email Preferences Functions
  const loadEmailPreferences = async () => {
    setLoadingEmailPrefs(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-reports/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmailPrefs(data);
      }
    } catch (err) {
      console.error('Failed to load email preferences:', err);
    } finally {
      setLoadingEmailPrefs(false);
    }
  };

  const updateEmailPreference = async (key, value) => {
    const newPrefs = { ...emailPrefs, [key]: value };
    setEmailPrefs(newPrefs);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-reports/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPrefs)
      });
      
      if (response.ok) {
        toast.success('Email preference updated');
      } else {
        toast.error('Failed to update preference');
        setEmailPrefs(emailPrefs); // Revert
      }
    } catch (err) {
      toast.error('Failed to update preference');
      setEmailPrefs(emailPrefs); // Revert
    }
  };

  const previewClientReport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-reports/preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setShowPreview(true);
      } else {
        toast.error('Failed to load preview');
      }
    } catch (err) {
      toast.error('Failed to load preview');
    }
  };

  const sendClientReport = async () => {
    setSendingReport(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-reports/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Report sent to your email!');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send report');
      }
    } catch (err) {
      toast.error('Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await authAPI.updateProfile({
        full_name: formData.full_name,
        email: formData.email,
        company_name: formData.company_name,
        phone: formData.phone,
      });
      toast.success('Profile updated successfully');
      if (refreshUser) await refreshUser();
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    // Client-side validation
    const password = passwordForm.new_password;
    if (password.length < 10) {
      toast.error('Password must be at least 10 characters long');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      toast.error('Password must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(password)) {
      toast.error('Password must contain at least one number');
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      toast.error('Password must contain at least one special character');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Reset form data when starting to edit
  const startEditing = () => {
    setFormData({
      full_name: user?.full_name || '',
      email: user?.email || '',
      company_name: user?.company_name || '',
      phone: user?.phone || '',
    });
    setEditing(true);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Profile Settings</h1>
          <p className="text-muted-foreground">Manage your account information</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Update your profile details</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Avatar Section */}
            <div className="flex items-center gap-6 mb-6 pb-6 border-b">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden border-4 border-background shadow-lg">
                  {getAvatarUrl() ? (
                    <img 
                      src={getAvatarUrl()} 
                      alt={user?.full_name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-12 h-12 text-muted-foreground" />
                  )}
                  {uploadingAvatar && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute bottom-0 right-0 p-2 bg-primary text-primary-foreground rounded-full shadow-md hover:bg-primary/90 transition-colors"
                  disabled={uploadingAvatar}
                  title="Change avatar"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{user?.full_name}</h3>
                <p className="text-muted-foreground text-sm">{user?.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {getAvatarUrl() ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  {getAvatarUrl() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteAvatar}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG or WebP. Max 2MB.
                </p>
              </div>
            </div>

            {!editing ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-4 p-4 bg-muted rounded-lg">
                  <User className="w-8 h-8 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Full Name</p>
                    <p className="font-semibold">{user?.full_name}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-muted rounded-lg">
                  <Mail className="w-8 h-8 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-semibold">{user?.email}</p>
                  </div>
                </div>
                {user?.company_name && (
                  <div className="flex items-center space-x-4 p-4 bg-muted rounded-lg">
                    <Building className="w-8 h-8 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Company</p>
                      <p className="font-semibold">{user.company_name}</p>
                    </div>
                  </div>
                )}
                {user?.phone && (
                  <div className="flex items-center space-x-4 p-4 bg-muted rounded-lg">
                    <Phone className="w-8 h-8 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-semibold">{user.phone}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center space-x-4 p-4 bg-muted rounded-lg">
                  <Shield className="w-8 h-8 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Account Type</p>
                    <p className="font-semibold capitalize">{user?.role}</p>
                  </div>
                </div>
                <Button onClick={startEditing} className="w-full" data-testid="edit-profile-button">
                  Edit Profile
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="profile-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    data-testid="profile-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+30 123 456 7890"
                    data-testid="profile-phone-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" className="flex-1" disabled={saving} data-testid="save-profile-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Account Security
            </CardTitle>
            <CardDescription>Manage your password and security settings</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                  placeholder="Enter your current password"
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  placeholder="Enter new password"
                  required
                  data-testid="new-password-input"
                />
                <div className="text-xs text-muted-foreground space-y-1 mt-2 p-3 bg-muted rounded-md">
                  <p className="font-medium">Password requirements:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li className={passwordForm.new_password.length >= 10 ? 'text-green-600' : ''}>
                      At least 10 characters
                    </li>
                    <li className={/[A-Z]/.test(passwordForm.new_password) ? 'text-green-600' : ''}>
                      One uppercase letter (A-Z)
                    </li>
                    <li className={/[a-z]/.test(passwordForm.new_password) ? 'text-green-600' : ''}>
                      One lowercase letter (a-z)
                    </li>
                    <li className={/[0-9]/.test(passwordForm.new_password) ? 'text-green-600' : ''}>
                      One number (0-9)
                    </li>
                    <li className={/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(passwordForm.new_password) ? 'text-green-600' : ''}>
                      One special character (!@#$%^&*...)
                    </li>
                  </ul>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
                {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
              </div>
              <Button type="submit" disabled={changingPassword} data-testid="change-password-btn">
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Payment Methods Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Methods
                </CardTitle>
                <CardDescription>Manage your saved payment methods</CardDescription>
              </div>
              <Button onClick={() => setShowAddCard(true)} size="sm" data-testid="add-payment-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Card
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingPayments ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No payment methods saved</p>
                <p className="text-sm">Add a card to enable auto-renewal for your services</p>
              </div>
            ) : (
              <div className="space-y-3">
                {paymentMethods.map((pm) => {
                  const brand = CARD_BRANDS[pm.brand] || CARD_BRANDS.default;
                  const expired = isCardExpired(pm.exp_month, pm.exp_year);
                  const expiringSoon = isCardExpiringSoon(pm.exp_month, pm.exp_year);
                  
                  return (
                    <div 
                      key={pm.id}
                      data-testid={`payment-method-${pm.id}`}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        expired ? 'bg-red-50 border-red-200' : 
                        expiringSoon ? 'bg-amber-50 border-amber-200' : 
                        'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Card Visual */}
                        <div className={`${brand.color} text-white rounded-lg p-3 min-w-[80px]`}>
                          <div className="text-[10px] opacity-80 mb-1">{brand.name}</div>
                          <div className="text-sm font-mono">•••• {pm.last_four}</div>
                          <div className="text-[10px] mt-1">{pm.exp_month.toString().padStart(2, '0')}/{pm.exp_year.toString().slice(-2)}</div>
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {brand.name} ending in {pm.last_four}
                            </span>
                            {pm.is_default && (
                              <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">
                                <Star className="w-3 h-3 mr-1" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {pm.cardholder_name && <span>{pm.cardholder_name} • </span>}
                            Expires {pm.exp_month.toString().padStart(2, '0')}/{pm.exp_year}
                          </div>
                          {expired && (
                            <div className="flex items-center gap-1 text-red-600 text-sm mt-1">
                              <AlertTriangle className="w-4 h-4" />
                              Card expired
                            </div>
                          )}
                          {expiringSoon && !expired && (
                            <div className="flex items-center gap-1 text-amber-600 text-sm mt-1">
                              <AlertTriangle className="w-4 h-4" />
                              Expiring soon
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {!pm.is_default && !expired && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSetDefault(pm.id)}
                            data-testid={`set-default-${pm.id}`}
                          >
                            Set Default
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeletePaymentMethod(pm.id)}
                          data-testid={`delete-payment-${pm.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Card Dialog */}
        <Dialog open={showAddCard} onOpenChange={setShowAddCard}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Payment Method</DialogTitle>
              <DialogDescription>
                Enter your card details to save for future payments
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleAddCard} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cardholder_name">Cardholder Name</Label>
                <Input
                  id="cardholder_name"
                  placeholder="Name on card"
                  value={newCard.cardholder_name}
                  onChange={(e) => setNewCard({...newCard, cardholder_name: e.target.value})}
                  data-testid="cardholder-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand">Card Type</Label>
                  <Select 
                    value={newCard.brand} 
                    onValueChange={(v) => setNewCard({...newCard, brand: v})}
                  >
                    <SelectTrigger data-testid="card-brand-select">
                      <SelectValue placeholder="Select card type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visa">Visa</SelectItem>
                      <SelectItem value="mastercard">Mastercard</SelectItem>
                      <SelectItem value="amex">American Express</SelectItem>
                      <SelectItem value="discover">Discover</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="last_four">Last 4 Digits</Label>
                  <Input
                    id="last_four"
                    placeholder="1234"
                    maxLength={4}
                    value={newCard.last_four}
                    onChange={(e) => setNewCard({...newCard, last_four: e.target.value.replace(/\D/g, '')})}
                    data-testid="last-four-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="exp_month">Expiry Month</Label>
                  <Select 
                    value={newCard.exp_month.toString()} 
                    onValueChange={(v) => setNewCard({...newCard, exp_month: v})}
                  >
                    <SelectTrigger data-testid="exp-month-select">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 12}, (_, i) => (
                        <SelectItem key={i+1} value={(i+1).toString()}>
                          {(i+1).toString().padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="exp_year">Expiry Year</Label>
                  <Select 
                    value={newCard.exp_year.toString()} 
                    onValueChange={(v) => setNewCard({...newCard, exp_year: v})}
                  >
                    <SelectTrigger data-testid="exp-year-select">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 10}, (_, i) => {
                        const year = new Date().getFullYear() + i;
                        return (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="set_default"
                  checked={newCard.set_as_default}
                  onChange={(e) => setNewCard({...newCard, set_as_default: e.target.checked})}
                  className="rounded border-slate-300"
                  data-testid="set-default-checkbox"
                />
                <Label htmlFor="set_default" className="text-sm font-normal">
                  Set as default payment method
                </Label>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddCard(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={addingCard} data-testid="save-card-btn">
                  {addingCard ? 'Saving...' : 'Save Card'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Email Preferences Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Email Preferences
            </CardTitle>
            <CardDescription>Manage your email notifications and reports</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingEmailPrefs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Preferences Toggles */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid="monthly-summary-pref">
                    <div>
                      <p className="font-medium">Monthly Account Summary</p>
                      <p className="text-sm text-muted-foreground">Receive a monthly summary of your services and account status</p>
                    </div>
                    <Switch
                      checked={emailPrefs.monthly_summary_enabled}
                      onCheckedChange={(checked) => updateEmailPreference('monthly_summary_enabled', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid="renewal-reminders-pref">
                    <div>
                      <p className="font-medium">Renewal Reminders</p>
                      <p className="text-sm text-muted-foreground">Get notified when your services are about to expire</p>
                    </div>
                    <Switch
                      checked={emailPrefs.renewal_reminders_enabled}
                      onCheckedChange={(checked) => updateEmailPreference('renewal_reminders_enabled', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-testid="promo-emails-pref">
                    <div>
                      <p className="font-medium">Promotional Emails</p>
                      <p className="text-sm text-muted-foreground">Receive special offers and product updates</p>
                    </div>
                    <Switch
                      checked={emailPrefs.promotional_emails_enabled}
                      onCheckedChange={(checked) => updateEmailPreference('promotional_emails_enabled', checked)}
                    />
                  </div>
                </div>

                {/* Send Report Now */}
                <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-100">
                  <h4 className="font-medium text-cyan-900 mb-2">📋 Monthly Report</h4>
                  <p className="text-sm text-cyan-700 mb-4">
                    Get an instant summary of your account including active services, upcoming renewals, and recommendations.
                  </p>
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={previewClientReport}
                      className="border-cyan-200 text-cyan-700 hover:bg-cyan-100"
                      data-testid="preview-report-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                    <Button 
                      size="sm"
                      onClick={sendClientReport}
                      disabled={sendingReport}
                      className="bg-cyan-600 hover:bg-cyan-700"
                      data-testid="send-report-btn"
                    >
                      {sendingReport ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send to My Email
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Your Account Summary</DialogTitle>
              <DialogDescription>
                This is how your monthly report will look
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-auto max-h-[70vh] border rounded-lg">
              {previewHtml && (
                <iframe 
                  srcDoc={previewHtml}
                  title="Report Preview"
                  className="w-full h-[600px] border-0"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}